import React from 'react'
import { css } from '@emotion/core'
import type { DisplayColumnSetting, RestaurantOverviewRow } from '@/models/restaurant-manager'
import { RestaurantCellLines } from './restaurant-cell-lines'
import { RestaurantCustomerTagsCell } from './restaurant-customer-tags-cell'
import { RestaurantMealNoteCell } from './restaurant-meal-note-cell'
import { RestaurantRoomNumberCell } from './restaurant-room-number-cell'
import { RestaurantMealSpaceCell } from './restaurant-meal-space-cell'
import { restaurantTableTooltipPanelStyle } from './restaurant-table-styles'
import { LinkedText } from '@/components/molecules/linked-text'
import { TruncateTooltip } from '@/components/molecules/truncate-tooltip'

type Props = {
  row: RestaurantOverviewRow
  columnKey: DisplayColumnSetting['columnKey']
  date: string
}

export const RestaurantCellRenderer: React.FC<Props> = ({ row, columnKey, date }) => {
  switch (columnKey) {
    case 'roomNumber':
      return <RestaurantRoomNumberCell rooms={row.roomNumber} />
    case 'guestName':
      return (
        <TruncateTooltip text={row.guestName} customPanelStyle={restaurantTableTooltipPanelStyle('guestName')}>
          <LinkedText
            guestName={row.guestName}
            reservationUuid={row.reservationUuid}
            reservationId={row.reservationId}
            isOpenInNewTab={true}
            customTextStyle={linkedTextInClampStyle}
            customLinkStyle={linkedTextInClampStyle}
          />
        </TruncateTooltip>
      )
    case 'customerTags':
      return <RestaurantCustomerTagsCell customerTags={row.customerTags} customerId={row.customerId} reservationId={row.reservationId} />
    case 'stayCount':
      return <>{row.stayCount ?? '-'}</>
    case 'planName':
      return <TruncateTooltip text={row.planName} customPanelStyle={restaurantTableTooltipPanelStyle('planName')} />
    case 'mealCondition':
      return <WrappedText value={row.mealCondition} />
    case 'accommodationDate':
      return <WrappedText value={row.accommodationDate} />
    case 'frontMemo':
      return <TruncateTooltip text={row.frontMemo} customPanelStyle={restaurantTableTooltipPanelStyle('frontMemo')} />
    case 'mealNote':
      return <RestaurantMealNoteCell reservationId={row.reservationId} mealNote={row.mealNote} />
    case 'spaceReservationDetails':
      return (
        <div css={mealSpaceCellStyle}>
          <RestaurantMealSpaceCell reservationId={row.reservationId} date={date} details={row.spaceReservationDetails} />
        </div>
      )
    case 'subjectName':
      return (
        <RestaurantCellLines lines={row.subjects.map(subject => ({ value: subject.subjectName, isHighlighted: subject.isHighlighted }))} />
      )
    // 泊目は強調表示の対象外
    case 'nights':
      return <RestaurantCellLines lines={row.subjects.map(subject => ({ value: subject.nights }))} />
    case 'unitPrice':
      return (
        <RestaurantCellLines
          lines={row.subjects.map(subject => ({
            value: subject.unitPrice == null ? '-' : subject.unitPrice.toLocaleString(),
            isHighlighted: subject.isHighlighted,
          }))}
        />
      )
    case 'quantity':
      return (
        <RestaurantCellLines
          lines={row.subjects.map(subject => ({ value: subject.quantity.toString(), isHighlighted: subject.isHighlighted }))}
        />
      )
    default:
      return <>-</>
  }
}

const WrappedText: React.FC<{ value: string | number | null | undefined }> = ({ value }) => <div css={wrappedTextStyle}>{value ?? '-'}</div>

// -webkit-box の中に antd の p { margin-bottom: 1em } が入ると行数の計算がずれるため打ち消す
const linkedTextInClampStyle = css({ margin: 0 })

const wrappedTextStyle = css({
  minWidth: 0,
  overflowWrap: 'anywhere',
  whiteSpace: 'pre-wrap',
  width: '100%',
  wordBreak: 'break-word',
  lineHeight: 1.5,
})

const mealSpaceCellStyle = css({
  display: 'flex',
  flexDirection: 'column',
  gap: 8,
  minWidth: 0,
  width: '100%',
})
